import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Circle,
  Rect,
  Defs,
  Stop,
  LinearGradient,
  Line,
  Font,
} from '@react-pdf/renderer';
import type { ScoreBreakdown, LeadershipFamilyCode, LeadershipTypeCode } from '@/types/equip360';
import { LEADERSHIP_FAMILIES, LEADERSHIP_TYPES, EQ_PILLARS, CULTURE_DIMENSIONS } from '@/types';
import {
  getMetricPercentage,
  getCultureRippleInsight,
  getBEDProfileInsight,
  getPressurePatternInsight,
  getMoveTheStoolInsight,
  getBecauseStatementInsight,
  getEQPillarInsight,
  getCultureDimensionInsight,
} from '@/utils';
import {
  getTierLabel,
  computeGapMeter,
  getROICards,
  getProgressionData,
  getLeadershipStory,
  getScoresConnectorInsight,
  getBEDStoryInsight,
  getEQPatternInsight,
  getPressureShadowName,
  getPressureAntidote,
  getMTSHeadline,
  getActionItems,
  BENCHMARKS,
  EQ_BENCHMARKS,
} from '@/utils/v2ReportData';

import CormorantRegular from '@/assets/fonts/CormorantGaramond-Regular.ttf';
import CormorantSemiBold from '@/assets/fonts/CormorantGaramond-SemiBold.ttf';
import CormorantBold from '@/assets/fonts/CormorantGaramond-Bold.ttf';
import CormorantItalic from '@/assets/fonts/CormorantGaramond-Italic.ttf';
import OutfitLight from '@/assets/fonts/Outfit-Light.ttf';
import OutfitRegular from '@/assets/fonts/Outfit-Regular.ttf';
import OutfitMedium from '@/assets/fonts/Outfit-Medium.ttf';
import OutfitSemiBold from '@/assets/fonts/Outfit-SemiBold.ttf';
import OutfitBold from '@/assets/fonts/Outfit-Bold.ttf';
import InconsolataRegular from '@/assets/fonts/Inconsolata-Regular.ttf';
import InconsolataBold from '@/assets/fonts/Inconsolata-Bold.ttf';

Font.register({
  family: 'Cormorant',
  fonts: [
    { src: CormorantRegular, fontWeight: 400 },
    { src: CormorantSemiBold, fontWeight: 600 },
    { src: CormorantBold, fontWeight: 700 },
    { src: CormorantItalic, fontWeight: 400, fontStyle: 'italic' },
  ],
});

Font.register({
  family: 'Outfit',
  fonts: [
    { src: OutfitLight, fontWeight: 300 },
    { src: OutfitRegular, fontWeight: 400 },
    { src: OutfitMedium, fontWeight: 500 },
    { src: OutfitSemiBold, fontWeight: 600 },
    { src: OutfitBold, fontWeight: 700 },
  ],
});

Font.register({
  family: 'Inconsolata',
  fonts: [
    { src: InconsolataRegular, fontWeight: 400 },
    { src: InconsolataBold, fontWeight: 700 },
  ],
});

function stripHtml(html: string): string {
  return html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '');
}

function getTierColorPDF(pct: number): string {
  if (pct >= 85) return GOLD;
  if (pct >= 70) return GREEN;
  if (pct >= 50) return AMBER;
  return RED;
}

function getBarEndColor(pct: number): string {
  if (pct >= 70) return GREEN;
  if (pct >= 50) return AMBER;
  return RED;
}

function getBarStartColor(pct: number): string {
  if (pct >= 70) return GREEN_DIM;
  if (pct >= 50) return '#A67D1A';
  return RED_DIM;
}

const PW = 842;
const PH = 595;
const PAD = 36;
const CONTENT_W = PW - PAD * 2;

const BG = '#07070D';
const BG2 = '#0E0E18';
const CARD = '#12121F';
const CARD_BORDER = 'rgba(255,255,255,0.06)';
const GOLD = '#E2C068';
const GOLD_DIM = '#6B5520';
const GREEN = '#3DB87A';
const GREEN_DIM = '#2D6B52';
const AMBER = '#E8A832';
const RED = '#D95555';
const RED_DIM = '#A03333';
const BLUE = '#6B84FF';
const TEXT1 = '#EEEEF8';
const TEXT2 = '#A0A0C0';
const TEXT3 = '#5C5C80';
const DIVIDER = 'rgba(255,255,255,0.06)';

const s = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 40,
    paddingHorizontal: PAD,
    fontFamily: 'Outfit',
    fontWeight: 400,
    backgroundColor: BG,
    fontSize: 8.5,
    color: TEXT1,
    lineHeight: 1.55,
  },
  coverPage: {
    padding: 0,
    backgroundColor: BG,
    fontFamily: 'Outfit',
  },
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 22,
    backgroundColor: BG2,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: GOLD_DIM,
    borderTopStyle: 'solid',
  },
  footerText: {
    fontSize: 5.5,
    color: GOLD_DIM,
    letterSpacing: 2,
    fontFamily: 'Inconsolata',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  eyebrow: {
    fontSize: 7.5,
    color: GOLD,
    letterSpacing: 2.5,
    textTransform: 'uppercase',
    fontFamily: 'Inconsolata',
    fontWeight: 700,
    marginBottom: 8,
  },
  card: {
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderStyle: 'solid',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  cardGold: {
    backgroundColor: CARD,
    borderWidth: 1,
    borderColor: GOLD_DIM,
    borderStyle: 'solid',
    borderLeftWidth: 3,
    borderLeftColor: GOLD,
    borderLeftStyle: 'solid',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  divider: {
    height: 1,
    backgroundColor: DIVIDER,
    marginVertical: 10,
  },
  section: {
    marginBottom: 10,
  },
  row: {
    flexDirection: 'row',
    gap: 8,
  },
  badge: {
    fontSize: 7,
    color: GOLD,
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: 'Inconsolata',
    fontWeight: 400,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: GOLD_DIM,
    borderStyle: 'solid',
    borderRadius: 10,
  },
  badgeGreen: {
    fontSize: 6.5,
    color: '#7DD4A8',
    letterSpacing: 0.5,
    fontFamily: 'Outfit',
    fontWeight: 500,
    paddingHorizontal: 5,
    paddingVertical: 2,
    backgroundColor: 'rgba(61,184,122,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61,184,122,0.25)',
    borderStyle: 'solid',
    borderRadius: 8,
  },
  badgeRed: {
    fontSize: 6.5,
    color: '#E08888',
    letterSpacing: 0.5,
    fontFamily: 'Outfit',
    fontWeight: 500,
    paddingHorizontal: 5,
    paddingVertical: 2,
    backgroundColor: 'rgba(217,85,85,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(217,85,85,0.25)',
    borderStyle: 'solid',
    borderRadius: 8,
  },
  badgeAmber: {
    fontSize: 6.5,
    color: '#F0C060',
    letterSpacing: 0.5,
    fontFamily: 'Outfit',
    fontWeight: 500,
    paddingHorizontal: 5,
    paddingVertical: 2,
    backgroundColor: 'rgba(232,168,50,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(232,168,50,0.25)',
    borderStyle: 'solid',
    borderRadius: 8,
  },
});

function PageFooter() {
  return (
    <View style={s.footer} fixed>
      <Text style={s.footerText}>E.Q.U.I.P. 360  —  Leadership Intelligence Assessment</Text>
    </View>
  );
}

function GradientBar({ percentage, pct, height = 7, benchmark, width }: { percentage: number; pct: number; height?: number; benchmark?: number; width?: number }) {
  const w = width ?? CONTENT_W;
  const fillW = Math.max(Math.min(percentage, 100) / 100 * w, 1);
  const gradId = `bar-${Math.random().toString(36).slice(2, 8)}`;
  const startColor = getBarStartColor(pct);
  const endColor = getBarEndColor(pct);

  return (
    <View style={{ marginBottom: 3 }}>
      <Svg width={w} height={height} viewBox={`0 0 ${w} ${height}`}>
        <Defs>
          <LinearGradient id={gradId} x1="0" y1="0" x2={String(fillW)} y2="0" gradientUnits="userSpaceOnUse">
            <Stop offset="0%" stopColor={startColor} />
            <Stop offset="100%" stopColor={endColor} />
          </LinearGradient>
        </Defs>
        <Rect x="0" y="0" width={String(w)} height={String(height)} rx="3" fill="rgba(255,255,255,0.05)" />
        <Rect x="0" y="0" width={String(fillW)} height={String(height)} rx="3" fill={`url(#${gradId})`} />
        {benchmark !== undefined && (
          <Line x1={String(benchmark / 100 * w)} y1="-1" x2={String(benchmark / 100 * w)} y2={String(height + 1)} stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
        )}
      </Svg>
    </View>
  );
}

function GradientScoreRing({ percentage, size = 160, strokeWidth = 9, label = 'Overall Score' }: {
  percentage: number; size?: number; strokeWidth?: number; label?: string;
}) {
  const r = (size / 2) - strokeWidth - 4;
  const circumference = 2 * Math.PI * r;
  const dashLength = (percentage / 100) * circumference;
  const gradId = `ring-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <View style={{ alignItems: 'center' }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Defs>
          <LinearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            <Stop offset="0%" stopColor={GOLD_DIM} />
            <Stop offset="100%" stopColor={GOLD} />
          </LinearGradient>
        </Defs>
        <Circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={strokeWidth} />
        <Circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={`url(#${gradId})`} strokeWidth={strokeWidth}
          strokeDasharray={`${dashLength} ${circumference}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </Svg>
      <View style={{ position: 'absolute', top: size * 0.28, alignItems: 'center' }}>
        <Text style={{ fontSize: size * 0.28, fontFamily: 'Inconsolata', fontWeight: 700, color: GOLD }}>{percentage}%</Text>
        <Text style={{ fontSize: size * 0.065, color: TEXT3, textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'Inconsolata' }}>{label}</Text>
      </View>
    </View>
  );
}

function DonutRing({ percentage, size = 48, strokeWidth = 4, color }: {
  percentage: number; size?: number; strokeWidth?: number; color: string;
}) {
  const r = (size / 2) - strokeWidth - 2;
  const circumference = 2 * Math.PI * r;
  const dashLength = (percentage / 100) * circumference;

  return (
    <View style={{ width: size, height: size, position: 'relative' }}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={strokeWidth} />
        <Circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={`${dashLength} ${circumference}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </Svg>
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ fontSize: size * 0.26, fontFamily: 'Inconsolata', fontWeight: 700, color }}>{percentage}</Text>
      </View>
    </View>
  );
}

function ProgressionRail({ overallPct, width }: { overallPct: number; width?: number }) {
  const railW = width ?? CONTENT_W;
  const railH = 10;
  const emW = railW * 0.40;
  const devW = railW * 0.25;
  const strW = railW * 0.20;
  const eliW = railW * 0.15;
  const youX = (overallPct / 100) * railW;
  const avgX = 0.50 * railW;
  const top20X = 0.80 * railW;

  return (
    <View style={{ marginBottom: 8 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 }}>
        <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 1, textTransform: 'uppercase' }}>0</Text>
        <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 1, textTransform: 'uppercase' }}>Emerging (0–49)</Text>
        <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 1, textTransform: 'uppercase' }}>Developing (50–69)</Text>
        <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 1, textTransform: 'uppercase' }}>Strong (70–84)</Text>
        <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 1, textTransform: 'uppercase' }}>Elite (85–100)</Text>
      </View>
      <Svg width={railW} height={railH + 24} viewBox={`0 0 ${railW} ${railH + 24}`}>
        <Rect x="0" y="0" width={String(emW)} height={String(railH)} rx="0" fill="rgba(217,85,85,0.25)" />
        <Rect x={String(emW)} y="0" width={String(devW)} height={String(railH)} rx="0" fill="rgba(232,168,50,0.25)" />
        <Rect x={String(emW + devW)} y="0" width={String(strW)} height={String(railH)} rx="0" fill="rgba(61,184,122,0.25)" />
        <Rect x={String(emW + devW + strW)} y="0" width={String(eliW)} height={String(railH)} rx="0" fill="rgba(200,168,75,0.35)" />
        <Rect x={String(avgX - 1)} y="-3" width="2" height={String(railH + 6)} fill="rgba(255,255,255,0.2)" rx="1" />
        <Rect x={String(top20X - 1)} y="-3" width="2" height={String(railH + 6)} fill="rgba(200,168,75,0.5)" rx="1" />
        <Rect x={String(youX - 3)} y="-6" width="6" height={String(railH + 12)} fill={GOLD} rx="3" />
      </Svg>
      <View style={{ flexDirection: 'row', position: 'relative', height: 10, marginTop: -12 }}>
        <Text style={{ position: 'absolute', left: Math.max(0, avgX - 20), fontSize: 5.5, fontFamily: 'Inconsolata', color: TEXT3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Industry Avg</Text>
        <Text style={{ position: 'absolute', left: Math.min(railW - 30, top20X - 10), fontSize: 5.5, fontFamily: 'Inconsolata', color: TEXT3, textTransform: 'uppercase', letterSpacing: 0.5 }}>Top 20%</Text>
        <Text style={{ position: 'absolute', left: Math.max(0, Math.min(railW - 15, youX - 6)), fontSize: 5.5, fontFamily: 'Inconsolata', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: 0.5 }}>YOU</Text>
      </View>
    </View>
  );
}

export interface AssessmentPDFData {
  scores: ScoreBreakdown;
  leadershipFamily: LeadershipFamilyCode;
  leadershipType: LeadershipTypeCode;
  userName: string;
  reportDate: string;
}

export function AssessmentPDFDocument({ data }: { data: AssessmentPDFData }) {
  const { scores, leadershipFamily: familyCode, leadershipType: typeCode, userName, reportDate } = data;
  const family = LEADERSHIP_FAMILIES[familyCode];
  const type = LEADERSHIP_TYPES[typeCode];
  const overallPct = scores.overall.percentage;
  const eqPct = scores.eq.percentage;
  const bedPct = scores.bed.percentage;
  const ciPct = scores.culture.percentage;

  const leadershipStory = stripHtml(getLeadershipStory(scores, familyCode, typeCode));
  const gapData = computeGapMeter(scores);
  const scoresConnector = stripHtml(getScoresConnectorInsight(scores));
  const eqPattern = stripHtml(getEQPatternInsight(scores));
  const bedInsights = getBEDProfileInsight(scores, typeCode);
  const bedStory = stripHtml(getBEDStoryInsight(scores));
  const roiCards = getROICards(scores);
  const cultureRipple = getCultureRippleInsight(scores, familyCode);
  const pressureInsight = getPressurePatternInsight(scores, typeCode);
  const shadowName = getPressureShadowName(typeCode);
  const antidote = getPressureAntidote(typeCode);
  const progressionData = getProgressionData(overallPct);
  const mtsHeadline = getMTSHeadline(scores, typeCode);
  const moveTheStool = getMoveTheStoolInsight(scores, typeCode);
  const becauseStatement = getBecauseStatementInsight(scores, typeCode, familyCode);
  const actionItems = getActionItems(scores, typeCode, familyCode);

  const eqPillarData = (['SA', 'SR', 'M', 'E', 'SS'] as const).map((code) => {
    const raw = scores.eq[code] as number;
    const pct = getMetricPercentage(raw);
    const pillar = EQ_PILLARS[code];
    const insight = getEQPillarInsight(code, pct, typeCode);
    const benchmark = EQ_BENCHMARKS[code];
    return { code, pct, name: pillar.name, insight, benchmark };
  });

  const cultureDimData = (['T', 'PS', 'CQ', 'TS', 'ER'] as const).map((code) => {
    const raw = scores.culture[code] as number;
    const pct = getMetricPercentage(raw);
    const dim = CULTURE_DIMENSIONS[code];
    const insight = getCultureDimensionInsight(code, pct, familyCode);
    return { code, pct, name: dim.name, fullName: dim.fullName, insight };
  });

  const bedData = [
    { code: 'B', label: 'Beliefs', raw: scores.bed.B, insight: bedInsights.beliefs },
    { code: 'EX', label: 'Excuses', raw: scores.bed.EX, insight: bedInsights.excuses },
    { code: 'D', label: 'Decisions', raw: scores.bed.D, insight: bedInsights.decisions },
  ].map((d) => ({ ...d, pct: getMetricPercentage(d.raw) }));

  const roiIconBgs: Record<string, { bg: string; border: string }> = {
    'roi-icon-gold': { bg: 'rgba(200,168,75,0.12)', border: 'rgba(200,168,75,0.2)' },
    'roi-icon-green': { bg: 'rgba(61,184,122,0.1)', border: 'rgba(61,184,122,0.2)' },
    'roi-icon-blue': { bg: 'rgba(61,92,245,0.1)', border: 'rgba(61,92,245,0.2)' },
    'roi-icon-amber': { bg: 'rgba(232,168,50,0.1)', border: 'rgba(232,168,50,0.2)' },
  };

  const halfW = (CONTENT_W - 8) / 2;
  const thirdW = (CONTENT_W - 16) / 3;
  const barInCard = halfW - 24;

  return (
    <Document>
      {/* PAGE 1: COVER — hero-grid 2-col layout */}
      <Page size={[PW, PH]} style={s.coverPage}>
        <PageFooter />
        <View style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, backgroundColor: GOLD }} />
        <View style={{ position: 'absolute', bottom: 22, left: 0, right: 0, height: 2, backgroundColor: GOLD }} />

        <View style={{ flexDirection: 'row', paddingHorizontal: 48, paddingTop: 50, paddingBottom: 10, flex: 1 }}>
          {/* Left: meta, name, title, badges, tagline */}
          <View style={{ flex: 1, justifyContent: 'center', paddingRight: 40 }}>
            <Text style={{ fontSize: 8, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
              E.Q.U.I.P. 360  ·  Leadership Intelligence  ·  {reportDate}
            </Text>
            <Text style={{ fontSize: 36, fontFamily: 'Cormorant', fontWeight: 700, color: GOLD, lineHeight: 1.05, marginBottom: 8 }}>
              {userName}
            </Text>
            <Text style={{ fontSize: 11, fontFamily: 'Outfit', color: TEXT2, letterSpacing: 0.5, marginBottom: 24 }}>
              Leadership Intelligence Assessment Report
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
              <Text style={s.badge}>{family.name} Family</Text>
              <Text style={[s.badge, { borderColor: 'rgba(61,92,245,0.35)', color: BLUE }]}>{type.name}</Text>
              <Text style={[s.badge, { borderColor: TEXT3, color: TEXT2 }]}>Overall: {overallPct}%</Text>
            </View>
            <View style={{ paddingLeft: 14, borderLeftWidth: 2, borderLeftColor: GOLD_DIM, borderLeftStyle: 'solid' }}>
              <Text style={{ fontSize: 13, fontFamily: 'Cormorant', fontStyle: 'italic', color: TEXT2, lineHeight: 1.5 }}>
                "Leadership is emotional.{'\n'}Because people are emotional."
              </Text>
            </View>
          </View>

          {/* Right: dial ring + tier + mini-scores */}
          <View style={{ width: 220, alignItems: 'center', justifyContent: 'center' }}>
            <GradientScoreRing percentage={overallPct} size={160} strokeWidth={9} label="Overall Score" />
            <View style={{ marginTop: 10, marginBottom: 14 }}>
              <Text style={[s.badge, { borderColor: getTierColorPDF(overallPct), color: getTierColorPDF(overallPct), fontSize: 7, letterSpacing: 1.5 }]}>
                {getTierLabel(overallPct)} Tier
              </Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 6, width: '100%' }}>
              {[
                { label: 'EQ Ready', pct: eqPct },
                { label: 'Behavioral', pct: bedPct },
                { label: 'Cultural', pct: ciPct },
              ].map((ms, i) => (
                <View key={i} style={{ flex: 1, backgroundColor: CARD, borderWidth: 1, borderColor: CARD_BORDER, borderStyle: 'solid', borderRadius: 6, padding: 8, alignItems: 'center' }}>
                  <Text style={{ fontSize: 16, fontFamily: 'Inconsolata', fontWeight: 700, color: getTierColorPDF(ms.pct), lineHeight: 1 }}>{ms.pct}%</Text>
                  <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: TEXT3, textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 2 }}>{ms.label}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </Page>

      {/* PAGE 2: WHY THIS MATTERS + LEADERSHIP STORY + IDENTITY */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={s.section}>
          <Text style={s.eyebrow}>Why This Matters</Text>
          <View style={[s.row, { gap: 6 }]}>
            {[
              { num: '10×', text: 'Leaders with high EQ are more than 10× as likely to score high on critical success factors', source: 'Six Seconds Global EQ Report, 2024' },
              { num: '90%', text: 'of top performers have high emotional intelligence — it separates average leaders from elite ones', source: 'TalentSmart Research' },
              { num: '58%', text: 'of your job performance is directly influenced by your emotional intelligence score', source: 'Workforce EQ Studies' },
            ].map((item, i) => (
              <View key={i} style={[s.card, { flex: 1, alignItems: 'center', textAlign: 'center', padding: 10 }]}>
                <Text style={{ fontSize: 22, fontFamily: 'Cormorant', fontWeight: 700, color: GOLD, marginBottom: 3 }}>{item.num}</Text>
                <Text style={{ fontSize: 6.5, color: TEXT2, lineHeight: 1.5, marginBottom: 3 }}>{item.text}</Text>
                <Text style={{ fontSize: 5.5, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 0.5, textTransform: 'uppercase' }}>{item.source}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={s.divider} />

        <View style={s.section}>
          <Text style={s.eyebrow}>Your Leadership Story</Text>
          <View style={s.cardGold}>
            <Text style={{ fontSize: 9.5, fontFamily: 'Cormorant', fontWeight: 400, color: '#D8D8EE', lineHeight: 1.75 }}>{leadershipStory}</Text>
          </View>
        </View>

        <View style={s.divider} />

        <View style={s.section}>
          <Text style={s.eyebrow}>Leadership Identity</Text>
          <View style={[s.row, { gap: 8 }]}>
            <View style={[s.card, { flex: 1, backgroundColor: 'rgba(61,184,122,0.06)', borderColor: 'rgba(61,184,122,0.2)' }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: GREEN }} />
                <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: GREEN, textTransform: 'uppercase', letterSpacing: 1.5 }}>The Leader They See</Text>
              </View>
              <Text style={{ fontSize: 15, fontFamily: 'Cormorant', fontWeight: 700, color: TEXT1, marginBottom: 3 }}>{type.name}</Text>
              <Text style={{ fontSize: 7.5, color: TEXT2, marginBottom: 6, lineHeight: 1.5 }}>When conditions are right, this is who shows up — the version your team remembers and follows.</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 3 }}>
                {type.strengths.map((str, i) => (
                  <Text key={i} style={s.badgeGreen}>{str}</Text>
                ))}
              </View>
            </View>
            <View style={[s.card, { flex: 1, backgroundColor: 'rgba(217,85,85,0.06)', borderColor: 'rgba(217,85,85,0.18)' }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5, marginBottom: 6 }}>
                <View style={{ width: 5, height: 5, borderRadius: 2.5, backgroundColor: RED }} />
                <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: RED, textTransform: 'uppercase', letterSpacing: 1.5 }}>The Leader Under Pressure</Text>
              </View>
              <Text style={{ fontSize: 15, fontFamily: 'Cormorant', fontWeight: 700, color: TEXT1, marginBottom: 3 }}>{shadowName}</Text>
              <Text style={{ fontSize: 7.5, color: TEXT2, marginBottom: 6, lineHeight: 1.5 }}>When stakes rise, this version emerges — the one your team braces for.</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 3 }}>
                {type.stressBehaviors.map((b, i) => (
                  <Text key={i} style={s.badgeRed}>{b}</Text>
                ))}
                {type.blindSpots.map((b, i) => (
                  <Text key={`bs${i}`} style={s.badgeAmber}>{b}</Text>
                ))}
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* PAGE 3: SCORE DASHBOARD (3 full-width cards) + GAP METER + EQ PILLARS */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={s.section}>
          <Text style={s.eyebrow}>Score Dashboard</Text>
          <View style={[s.row, { gap: 8, marginBottom: 6 }]}>
            {[
              { label: 'Emotional Readiness', pct: eqPct, bench: BENCHMARKS.ER, color: getTierColorPDF(eqPct) },
              { label: 'Behavioral Reality', pct: bedPct, bench: BENCHMARKS.BR, color: getTierColorPDF(bedPct) },
              { label: 'Cultural Influence', pct: ciPct, bench: BENCHMARKS.CI, color: getTierColorPDF(ciPct) },
            ].map((item, i) => (
              <View key={i} style={[s.card, { flex: 1, padding: 10, marginBottom: 0 }]}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <Text style={{ fontSize: 8, color: TEXT2 }}>{item.label}</Text>
                  <Text style={{ fontSize: 16, fontFamily: 'Inconsolata', fontWeight: 700, color: item.color }}>{item.pct}%</Text>
                </View>
                <GradientBar percentage={item.pct} pct={item.pct} benchmark={item.bench.high} height={6} width={thirdW - 24} />
                <Text style={{ fontSize: 5.5, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 0.5, marginTop: 2 }}>Benchmark: {item.bench.low}–{item.bench.high}%</Text>
              </View>
            ))}
          </View>
          <View style={[s.cardGold, { padding: 8 }]}>
            <Text style={{ fontSize: 7.5, color: TEXT1, lineHeight: 1.5 }}>{scoresConnector}</Text>
          </View>
        </View>

        <View style={s.divider} />

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <View style={[s.card, { flex: 1 }]}>
            <Text style={{ fontSize: 10, fontFamily: 'Cormorant', fontWeight: 700, color: TEXT1, marginBottom: 2 }}>Identity vs. Reputation Gap</Text>
            <Text style={{ fontSize: 6.5, color: TEXT2, marginBottom: 6, lineHeight: 1.5 }}>The gap between how you see yourself and how your team experiences you.</Text>
            <View style={{ marginBottom: 5 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                <Text style={{ fontSize: 6.5, fontFamily: 'Outfit', fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5 }}>How you see yourself</Text>
                <Text style={{ fontSize: 8, fontFamily: 'Inconsolata', fontWeight: 700, color: GREEN }}>{gapData.selfPerception}%</Text>
              </View>
              <GradientBar percentage={gapData.selfPerception} pct={70} height={5} width={barInCard} />
            </View>
            <View style={{ marginBottom: 5 }}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 2 }}>
                <Text style={{ fontSize: 6.5, fontFamily: 'Outfit', fontWeight: 600, color: TEXT2, textTransform: 'uppercase', letterSpacing: 0.5 }}>How team likely sees you</Text>
                <Text style={{ fontSize: 8, fontFamily: 'Inconsolata', fontWeight: 700, color: RED }}>~{gapData.teamEstimate}%</Text>
              </View>
              <GradientBar percentage={gapData.teamEstimate} pct={30} height={5} width={barInCard} />
            </View>
            {gapData.gap > 0 && (
              <View style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 4, padding: 5, marginTop: 2 }}>
                <Text style={{ fontSize: 6.5, fontFamily: 'Outfit', fontWeight: 600, color: GOLD, marginBottom: 1 }}>The Gap Insight</Text>
                <Text style={{ fontSize: 6.5, color: TEXT2, lineHeight: 1.5 }}>
                  A {gapData.gap}-point gap is common but closeable. Every point you close translates directly to a stronger team reputation.
                </Text>
              </View>
            )}
          </View>

          <View style={{ flex: 1 }}>
            <Text style={s.eyebrow}>EQ Pillars</Text>
            {eqPillarData.map((pillar) => (
              <View key={pillar.code} style={[s.card, { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 7, marginBottom: 4 }]} wrap={false}>
                <View style={{ width: 100 }}>
                  <Text style={{ fontSize: 7.5, fontFamily: 'Outfit', fontWeight: 600, color: TEXT1 }}>{pillar.name}</Text>
                  <Text style={{ fontSize: 5, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 0.5, marginTop: 1 }}>Bench: {pillar.benchmark}%</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <GradientBar percentage={pillar.pct} pct={pillar.pct} benchmark={pillar.benchmark} height={5} width={barInCard - 140} />
                </View>
                <Text style={{ fontSize: 12, fontFamily: 'Inconsolata', fontWeight: 700, color: getTierColorPDF(pillar.pct), width: 35, textAlign: 'right' }}>{pillar.pct}%</Text>
              </View>
            ))}
            <View style={[s.cardGold, { marginTop: 2, padding: 7 }]}>
              <Text style={{ fontSize: 6.5, fontFamily: 'Outfit', fontWeight: 600, color: GOLD, marginBottom: 1 }}>EQ Pattern</Text>
              <Text style={{ fontSize: 7, color: TEXT1, lineHeight: 1.5 }}>{eqPattern}</Text>
            </View>
          </View>
        </View>
      </Page>

      {/* PAGE 4: B.E.D. PROFILE + LEADERSHIP ROI */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={s.section}>
          <Text style={s.eyebrow}>B.E.D. Profile</Text>
          <View style={[s.row, { gap: 8 }]}>
            {bedData.map((item) => (
              <View key={item.code} style={[s.card, { flex: 1, borderLeftWidth: 3, borderLeftColor: GOLD, borderLeftStyle: 'solid' }]} wrap={false}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                  <Text style={{ fontSize: 7, fontFamily: 'Inconsolata', fontWeight: 400, color: TEXT3, letterSpacing: 1.5, textTransform: 'uppercase' }}>{item.label}</Text>
                  <Text style={{ fontSize: 18, fontFamily: 'Cormorant', fontWeight: 700, color: GOLD }}>{item.pct}%</Text>
                </View>
                <GradientBar percentage={item.pct} pct={item.pct} height={5} width={thirdW - 24} />
                <Text style={{ fontSize: 7, color: TEXT2, lineHeight: 1.45, marginTop: 2 }}>{item.insight}</Text>
                {item.code === 'EX' && item.pct <= 10 && (
                  <View style={{ marginTop: 4, padding: 6, backgroundColor: 'rgba(200,168,75,0.1)', borderWidth: 1, borderColor: 'rgba(200,168,75,0.25)', borderStyle: 'solid', borderRadius: 4 }}>
                    <Text style={{ fontSize: 6.5, fontFamily: 'Outfit', fontWeight: 500, color: GOLD }}>⚡ Outlier — Requires deeper reflection. Are you radically accountable, or absorbing blame?</Text>
                  </View>
                )}
              </View>
            ))}
          </View>
          <View style={[s.cardGold, { padding: 8 }]}>
            <Text style={{ fontSize: 7, fontFamily: 'Outfit', fontWeight: 600, color: GOLD, marginBottom: 1 }}>B.E.D. Story</Text>
            <Text style={{ fontSize: 7.5, color: TEXT1, lineHeight: 1.5 }}>{bedStory}</Text>
          </View>
        </View>

        <View style={s.divider} />

        <View style={s.section}>
          <Text style={s.eyebrow}>Leadership ROI</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {roiCards.map((card, i) => {
              const iconStyle = roiIconBgs[card.iconClass] || roiIconBgs['roi-icon-gold'];
              return (
                <View key={i} style={[s.card, { width: halfW, flexDirection: 'row', gap: 8, padding: 10, marginBottom: 0 }]} wrap={false}>
                  <View style={{ width: 28, height: 28, borderRadius: 6, backgroundColor: iconStyle.bg, borderWidth: 1, borderColor: iconStyle.border, borderStyle: 'solid', justifyContent: 'center', alignItems: 'center' }}>
                    <Text style={{ fontSize: 12 }}>{card.icon}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 17, fontFamily: 'Cormorant', fontWeight: 700, color: GOLD, marginBottom: 1 }}>{card.statNum}</Text>
                    <Text style={{ fontSize: 6.5, color: TEXT2, lineHeight: 1.4, marginBottom: 2 }}>{card.label}</Text>
                    <Text style={{ fontSize: 6.5, color: TEXT3, backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 3, paddingHorizontal: 4, paddingVertical: 1 }}>{stripHtml(card.yourScore)}</Text>
                    <Text style={{ fontSize: 5.5, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 2 }}>{card.source}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      </Page>

      {/* PAGE 5: CULTURE IMPACT + PRESSURE PATTERN */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={s.section}>
          <Text style={s.eyebrow}>Culture Impact Index</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
            {cultureDimData.map((dim) => (
              <View key={dim.code} style={[s.card, { width: halfW, flexDirection: 'row', gap: 8, alignItems: 'center', padding: 10, marginBottom: 0 }]} wrap={false}>
                <DonutRing percentage={dim.pct} size={40} strokeWidth={3.5} color={getTierColorPDF(dim.pct)} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 8, fontFamily: 'Outfit', fontWeight: 600, color: TEXT1, marginBottom: 1 }}>{dim.fullName}</Text>
                  <Text style={{ fontSize: 6.5, color: TEXT2, lineHeight: 1.45 }}>{dim.insight}</Text>
                </View>
              </View>
            ))}
          </View>
          <View style={{ backgroundColor: 'rgba(61,184,122,0.05)', borderWidth: 1, borderColor: 'rgba(61,184,122,0.18)', borderStyle: 'solid', borderRadius: 6, padding: 10 }}>
            <Text style={{ fontSize: 8.5, fontFamily: 'Cormorant', fontStyle: 'italic', color: '#A8D8C0', lineHeight: 1.65, marginBottom: 3 }}>{cultureRipple}</Text>
            <Text style={{ fontSize: 6, fontFamily: 'Inconsolata', color: GREEN, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.7 }}>Culture Impact Analysis · E.Q.U.I.P. 360</Text>
          </View>
        </View>

        <View style={s.divider} />

        <View style={s.section}>
          <Text style={s.eyebrow}>Pressure Pattern</Text>
          <View style={[s.card, { backgroundColor: 'rgba(217,85,85,0.04)', borderColor: 'rgba(217,85,85,0.18)' }]}>
            <View style={{ flexDirection: 'row', gap: 14 }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, fontFamily: 'Cormorant', fontWeight: 700, color: TEXT1, marginBottom: 6 }}>When the Heat Rises</Text>
                <Text style={{ fontSize: 7.5, color: TEXT2, lineHeight: 1.6, marginBottom: 8 }}>{pressureInsight}</Text>
                {type.stressBehaviors.map((b, i) => (
                  <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 3, padding: 5, backgroundColor: 'rgba(217,85,85,0.08)', borderWidth: 1, borderColor: 'rgba(217,85,85,0.18)', borderStyle: 'solid', borderRadius: 4 }}>
                    <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: RED, marginRight: 6 }} />
                    <Text style={{ fontSize: 7, color: '#E08888' }}>{b}</Text>
                  </View>
                ))}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', fontWeight: 400, color: TEXT3, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>Early Warning Signs</Text>
                {type.blindSpots.map((b, i) => (
                  <View key={i} style={{ marginBottom: 4, padding: 6, backgroundColor: 'rgba(232,168,50,0.07)', borderWidth: 1, borderColor: 'rgba(232,168,50,0.18)', borderStyle: 'solid', borderRadius: 4 }}>
                    <Text style={{ fontSize: 7, color: '#F0C060', lineHeight: 1.4 }}>Watch for: {b}</Text>
                  </View>
                ))}
                <View style={{ padding: 8, backgroundColor: 'rgba(61,184,122,0.07)', borderWidth: 1, borderColor: 'rgba(61,184,122,0.2)', borderStyle: 'solid', borderLeftWidth: 3, borderLeftColor: GREEN, borderLeftStyle: 'solid', borderRadius: 4, marginTop: 4 }}>
                  <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: GREEN, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>The Antidote</Text>
                  <Text style={{ fontSize: 7, color: TEXT2, lineHeight: 1.5 }}>{antidote}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* PAGE 6: PROGRESSION */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={s.section}>
          <Text style={s.eyebrow}>Your Leadership Progression</Text>
          <Text style={{ fontSize: 18, fontFamily: 'Cormorant', fontWeight: 700, color: TEXT1, marginBottom: 3 }}>Where You Are. Where You're Going.</Text>
          <Text style={{ fontSize: 7.5, color: TEXT2, marginBottom: 10, lineHeight: 1.5 }}>Not just who you are today — but exactly what changes when you reach the next level.</Text>

          <ProgressionRail overallPct={overallPct} />

          <View style={{ flexDirection: 'row', gap: 8, marginTop: 6 }}>
            <View style={[s.card, { flex: 1, backgroundColor: 'rgba(200,168,75,0.05)', borderColor: 'rgba(200,168,75,0.3)' }]}>
              <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: AMBER, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>Current — {overallPct}% Overall</Text>
              <Text style={{ fontSize: 22, fontFamily: 'Cormorant', fontWeight: 700, color: AMBER, marginBottom: 3 }}>{progressionData.currentTier}</Text>
              <Text style={{ fontSize: 8, fontFamily: 'Outfit', fontWeight: 600, color: TEXT1, marginBottom: 6 }}>The Consistent Leader</Text>
              {[
                `${ciPct >= 70 ? 'Strong' : 'Growing'} cultural presence`,
                `Awareness and regulation are ${eqPillarData[0].pct >= 70 && eqPillarData[1].pct >= 70 ? 'your anchors' : 'developing'}`,
                `Drive is ${eqPillarData[2].pct >= 70 ? 'consistent' : 'situational'}`,
                `Under pressure: ${type.stressBehaviors[0]?.toLowerCase()}`,
              ].map((trait, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 3 }}>
                  <Text style={{ fontSize: 7, color: AMBER, marginRight: 5 }}>→</Text>
                  <Text style={{ fontSize: 7, color: TEXT2, lineHeight: 1.4 }}>{trait}</Text>
                </View>
              ))}
            </View>
            <View style={[s.card, { flex: 1, backgroundColor: 'rgba(61,92,245,0.05)', borderColor: 'rgba(61,92,245,0.25)' }]}>
              <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: BLUE, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>Next Level — {progressionData.nextTierThreshold}%+ Overall</Text>
              <Text style={{ fontSize: 22, fontFamily: 'Cormorant', fontWeight: 700, color: BLUE, marginBottom: 3 }}>{progressionData.nextTier}</Text>
              <Text style={{ fontSize: 8, fontFamily: 'Outfit', fontWeight: 600, color: TEXT1, marginBottom: 6 }}>The Transformational Leader</Text>
              {[
                'Drive is internal — purpose-fueled, not mood-dependent',
                'Pressure triggers curiosity instead of reaction',
                'Accountability creates energy, not exhaustion',
                'People model their behavior after yours by choice',
              ].map((trait, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 3 }}>
                  <Text style={{ fontSize: 7, color: BLUE, marginRight: 5 }}>→</Text>
                  <Text style={{ fontSize: 7, color: TEXT2, lineHeight: 1.4 }}>{trait}</Text>
                </View>
              ))}
            </View>
          </View>

          <View style={[s.card, { marginTop: 6, padding: 10 }]}>
            <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: GOLD, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4 }}>The Distance</Text>
            <Text style={{ fontSize: 7.5, color: TEXT2, lineHeight: 1.6 }}>
              You are <Text style={{ fontFamily: 'Outfit', fontWeight: 600, color: TEXT1 }}>{progressionData.pointsToNext} points away</Text> from the {progressionData.nextTier} tier. Leaders who implement their Move the Stool action consistently move an average of <Text style={{ color: GOLD }}>8–12 points</Text> within 90 days.
            </Text>
          </View>
        </View>
      </Page>

      {/* PAGE 7: MOVE THE STOOL + 30-DAY CHALLENGE */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={s.section}>
          <Text style={s.eyebrow}>Move the Stool</Text>
          <View style={{ backgroundColor: 'rgba(200,168,75,0.06)', borderWidth: 1, borderColor: 'rgba(200,168,75,0.22)', borderStyle: 'solid', borderRadius: 8, padding: 18, marginBottom: 8 }}>
            <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', color: GOLD, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Move the Stool</Text>
            <Text style={{ fontSize: 16, fontFamily: 'Cormorant', fontWeight: 700, color: GOLD, marginBottom: 6 }}>{mtsHeadline.replace('\n', ' ')}</Text>
            <Text style={{ fontSize: 8, color: TEXT2, lineHeight: 1.75 }}>{moveTheStool}</Text>
          </View>
        </View>

        <View style={s.divider} />

        <View style={s.section}>
          <Text style={s.eyebrow}>Your 30-Day Challenge</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {actionItems.slice(0, 4).map((item, i) => (
              <View key={i} style={[s.card, { width: halfW, flexDirection: 'row', gap: 10, padding: 10, marginBottom: 0 }]} wrap={false}>
                <Text style={{ fontSize: 22, fontFamily: 'Cormorant', fontWeight: 700, color: GOLD, opacity: 0.35, lineHeight: 1 }}>{item.num}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 8.5, fontFamily: 'Outfit', fontWeight: 600, color: TEXT1, marginBottom: 2 }}>{item.headline}</Text>
                  <Text style={{ fontSize: 7, color: TEXT2, lineHeight: 1.5, marginBottom: 2 }}>{item.desc}</Text>
                  <Text style={{ fontSize: 5.5, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 0.5, textTransform: 'uppercase' }}>{item.tied}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </Page>

      {/* PAGE 8: BECAUSE STATEMENT + CLOSING */}
      <Page size={[PW, PH]} style={s.page}>
        <PageFooter />

        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 60 }}>
          <View style={s.section}>
            <Text style={[s.eyebrow, { textAlign: 'center' }]}>Your Leadership Because</Text>
            <View style={[s.cardGold, { padding: 20 }]}>
              <Text style={{ fontSize: 12, fontFamily: 'Cormorant', fontStyle: 'italic', color: TEXT1, lineHeight: 1.8, textAlign: 'center' }}>
                "{becauseStatement}"
              </Text>
            </View>
          </View>

          <View style={[s.divider, { width: '50%' }]} />

          <View style={{ textAlign: 'center', alignItems: 'center', marginTop: 20 }}>
            <Text style={{ fontSize: 12, fontFamily: 'Cormorant', fontStyle: 'italic', color: TEXT2, textAlign: 'center', marginBottom: 8, lineHeight: 1.8 }}>
              "Lead from emotional truth, not emotional convenience."
            </Text>
            <Text style={{ fontSize: 6.5, fontFamily: 'Inconsolata', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: 3, marginBottom: 16 }}>
              The E.Q.U.I.P. 360 Leadership Mandate
            </Text>

            <View style={{ width: 200, height: 1, backgroundColor: DIVIDER, marginBottom: 16 }} />

            <Text style={{ fontSize: 18, fontFamily: 'Inconsolata', fontWeight: 700, color: GOLD, letterSpacing: 3, marginBottom: 3 }}>
              E.Q.U.I.P. 360
            </Text>
            <Text style={{ fontSize: 7, fontFamily: 'Inconsolata', color: TEXT3, letterSpacing: 2 }}>
              Emotional Quotient Under Intelligent Pressure
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
